// One note, shown to a stranger.
//
// This is the only screen in Perfect Note drawn for somebody who is not signed
// in and is not being asked to be, and almost everything about it follows from
// that one sentence:
//
//   IT IS NOT A SCREEN IN THE SHELL. It is chosen in `main.tsx`, above `App`, so
//   the tab bar, the sidebar, the toast and - crucially - `useDayScreen`'s first
//   request are never mounted at all. Hanging it off `shell/screens.ts` would
//   have meant a stranger's browser asking `GET /days/today`, taking a 401 and
//   being shown the sign-in gate for a link somebody sent them.
//
//   IT OFFERS NOTHING. No sign-in button, no "make your own", no link into the
//   application. A read-only link is an answer to "can you read this", and every
//   control that is not the note is an answer to a question nobody asked. The
//   one exception is Try again, and only on the one failure trying again can fix.
//
//   IT SAYS WHAT IT IS, PLAINLY. The reader is told this is a read-only copy and
//   that whoever shared it can turn the link off; the OWNER is told, on the
//   button that makes it, that anyone with the link can read it. Both halves are
//   deliberate: people misjudge "unlisted" as "private", and wording is the only
//   place that misunderstanding can be corrected.
//
//   IT SAYS NOTHING ABOUT WHO WROTE IT. Not a name, not a date, not a group, not
//   a schedule - the payload carries three fields and this draws all three
//   (`api/src/routes/shared-notes.routes.ts` says why each absence is an
//   absence).
//
// THE BODY USES THE OWNER'S READ-VIEW RENDERER. A shared note must never expose
// formatting markers that its author sees as headings, lists, or emphasis, so
// both surfaces map the same parser output to React elements. There is still no
// HTML string and no sanitizer. This page's `<h1>` means its first note heading
// starts at `<h2>`.

import { useEffect } from 'react';
import { NoteMarkdown } from '../notes/NoteMarkdown.tsx';
import { useSharedNote } from './useSharedNote.ts';

/**
 * Tell crawlers no, from inside the page.
 *
 * THE THIRD OF THREE LAYERS, and the only one that survives the application
 * being served by something that is not this server. The other two are HTTP
 * headers: `X-Robots-Tag` on the HTML (api/src/http/error-handler.ts) and on the
 * API response (api/src/routes/shared-notes.routes.ts). A crawler that runs no
 * JavaScript never sees this one and does not need to; a build dropped on a
 * plain static host has only this one.
 *
 * Written rather than read: `index.html` deliberately carries no `robots` meta,
 * because the front door is the one page in this product worth indexing.
 */
function refuseIndexing(): void {
  const existing = document.querySelector('meta[name="robots"]');
  const meta = existing ?? document.createElement('meta');
  meta.setAttribute('name', 'robots');
  meta.setAttribute('content', 'noindex, nofollow');
  if (existing === null) document.head.appendChild(meta);
}

export function SharedNote({ token }: { token: string }) {
  const { state, retry } = useSharedNote(token);

  // On mount, before anything is drawn and whatever the link turns out to be: a
  // dead link must not be indexed either.
  useEffect(() => {
    refuseIndexing();
  }, []);

  // The note's own heading, so a reader with three of these open can tell them
  // apart. It is the note that was published, so it is already as public as the
  // page; nothing about its author goes into it.
  useEffect(() => {
    if (state.kind === 'note') document.title = `${state.note.heading} - Perfect Note`;
  }, [state]);

  return (
    <div className="sharepage">
      <div className="sharewrap">
        {state.kind === 'loading' && (
          <p className="sharewait" role="status">
            Loading…
          </p>
        )}

        {state.kind === 'note' && (
          <article className="sharecard">
            {/* The server's own `heading`: the title, or the first line standing
                in for it, or "Untitled note" - the same words the author sees
                beside this note in their own list, rather than a second answer
                worked out here. */}
            <h1>{state.note.heading}</h1>
            {/* The same React-element renderer as the owner's opt-in Read view.
                Empty is a real note and gets that renderer's ordinary empty
                state rather than looking as though this page failed to load. */}
            <div className="sharebody">
              <NoteMarkdown body={state.note.body} base={2} />
            </div>
          </article>
        )}

        {state.kind === 'gone' && (
          <div className="sharefail">
            <h1>This link does not work.</h1>
            {/* Deliberately does not say WHICH. A revoked link, a wrong link and
                a link to a deleted note are one answer here, because ranking
                them would be a way to ask which guess was closer - and because
                the honest thing to tell a reader is that the person who shared
                it is the person who can help. */}
            <p>
              It may have been turned off by whoever shared it, or the address may be incomplete.
              Ask them for it again.
            </p>
          </div>
        )}

        {state.kind === 'failed' && (
          <div className="sharefail">
            <h1>This note could not be loaded.</h1>
            {/* The only state with a button, because it is the only one where
                pressing it can end differently. A dead link offering Try again
                would be a loop with no exit - the same defect the sign-in gate
                closed on the day screen. */}
            <p role="alert">{state.problem}</p>
            <button className="btn" onClick={retry}>
              Try again
            </button>
          </div>
        )}

        {/* Under every state, including the failures: somebody who followed a
            link out of a message deserves to know what they have arrived at.
            It names the PRODUCT and never the author, and it is not a link -
            this page leads nowhere, and a stranger reading somebody's note is
            not somebody to advertise at. */}
        <p className="sharefoot">
          A read-only note shared from Perfect Note. Whoever shared it can turn the link off at any
          time.
        </p>
      </div>
    </div>
  );
}
